import { PageComponent, COMPONENT_TEMPLATES } from "../types/components";
import { generateId } from "./utils";

/**
 * Helpers para operar na árvore de componentes da página.
 *
 * Componentes do tipo "container" guardam componentes-filhos em
 * `config.children`. Estas funções percorrem essa árvore (topo + filhos
 * aninhados) para que os handlers do builder/runtime continuem funcionando
 * por id, independentemente de o componente estar no topo ou dentro de um
 * container.
 */

/** Retorna os filhos de um componente, se ele for um container. */
function getChildren(component: PageComponent): PageComponent[] | undefined {
  const children = component.config?.children as PageComponent[] | undefined;
  return Array.isArray(children) ? children : undefined;
}

/** Busca um componente por id em toda a árvore (inclui filhos de containers). */
export function findComponentDeep(
  components: PageComponent[],
  id: string,
): PageComponent | undefined {
  for (const component of components) {
    if (component.id === id) return component;
    const children = getChildren(component);
    if (children) {
      const found = findComponentDeep(children, id);
      if (found) return found;
    }
  }
  return undefined;
}

/**
 * Retorna uma nova árvore com o componente de `id` substituído pelo resultado
 * de `updater`. Mantém a mesma referência do array quando nada muda naquele
 * nível, evitando re-renders desnecessários nos ramos não afetados.
 */
export function updateComponentDeep(
  components: PageComponent[],
  id: string,
  updater: (component: PageComponent) => PageComponent,
): PageComponent[] {
  let changed = false;
  const result = components.map((component) => {
    if (component.id === id) {
      changed = true;
      return updater(component);
    }
    const children = getChildren(component);
    if (children && children.length > 0) {
      const newChildren = updateComponentDeep(children, id, updater);
      if (newChildren !== children) {
        changed = true;
        return {
          ...component,
          config: { ...component.config, children: newChildren },
        };
      }
    }
    return component;
  });
  return changed ? result : components;
}

/** Remove um componente por id em qualquer nível da árvore. */
export function removeComponentDeep(
  components: PageComponent[],
  id: string,
): PageComponent[] {
  let changed = false;
  const result: PageComponent[] = [];
  for (const component of components) {
    if (component.id === id) {
      changed = true;
      continue;
    }
    const children = getChildren(component);
    if (children && children.length > 0) {
      const newChildren = removeComponentDeep(children, id);
      if (newChildren !== children) {
        changed = true;
        result.push({
          ...component,
          config: { ...component.config, children: newChildren },
        });
        continue;
      }
    }
    result.push(component);
  }
  return changed ? result : components;
}

/** Adiciona um filho ao final da lista de filhos de um container. */
export function addChildToContainer(
  components: PageComponent[],
  containerId: string,
  child: PageComponent,
): PageComponent[] {
  return updateComponentDeep(components, containerId, (container) => ({
    ...container,
    config: {
      ...container.config,
      children: [...(getChildren(container) || []), child],
    },
  }));
}

/**
 * Move um componente existente (de qualquer nível) para dentro de um container.
 * Não aninha containers dentro de containers e ignora movimentos inválidos.
 */
export function moveComponentIntoContainer(
  components: PageComponent[],
  componentId: string,
  containerId: string,
): PageComponent[] {
  if (componentId === containerId) return components;
  const moving = findComponentDeep(components, componentId);
  if (!moving) return components;
  // Sem aninhar containers dentro de containers nesta versão.
  if (moving.type === "container") return components;
  const without = removeComponentDeep(components, componentId);
  return addChildToContainer(without, containerId, moving);
}

/**
 * Move um componente para o nível raiz da página (tira de dentro de um
 * container). No-op se já estiver na raiz.
 */
export function moveComponentToRoot(
  components: PageComponent[],
  componentId: string,
): PageComponent[] {
  const isAlreadyRoot = components.some((c) => c.id === componentId);
  if (isAlreadyRoot) return components;
  const moving = findComponentDeep(components, componentId);
  if (!moving) return components;
  const without = removeComponentDeep(components, componentId);
  // y alto + compactação vertical do grid posiciona no fim da página.
  return [
    ...without,
    { ...moving, position: { ...moving.position, x: 0, y: 9999 } },
  ];
}

/** Cria um novo componente a partir do id de um template da paleta. */
export function createComponentFromTemplate(
  templateId: string,
): PageComponent | null {
  const template = COMPONENT_TEMPLATES.find((t) => t.id === templateId);
  if (!template) return null;
  return {
    id: generateId(),
    type: template.type,
    position: {
      x: 0,
      y: 0,
      w: template.defaultSize.w,
      h: template.defaultSize.h,
    },
    config: JSON.parse(JSON.stringify(template.defaultConfig)),
  };
}
